import z from 'zod';

////////
// Core shared types used amongst multiple entities
////////
export const entitySchema = z.object({
    id: z.number().int(),
});

export const auditableSchema = z.object({
    createdDate: z.coerce.date(),
    createdByUserId: z.number().int(),
    modifiedDate: z.coerce.date().optional().nullable(),
    modifiedByUserId: z.number().int().optional().nullable(),
});

////////
// Core shared types used amongst multiple api requests/responses
////////
export const paginationParamsSchema = z.object({
    pageNumber: z.number().int(),
    pageSize: z.number().int(),
    sortBy: z.string().nullable().optional(),
    sortDir: z.string().nullable().optional(),
});

export const responseBaseSchema = z.object({
    isError: z.boolean(),
    errors: z.array(z.object({ message: z.string() })),
});

export const mutationResponseSchema = z.looseObject({
    ...entitySchema.shape,
    ...responseBaseSchema.shape,
});

// A generic builder function to create a paginated response schema
export const PaginatedResponseBuilder = <T extends z.ZodTypeAny>(itemSchema: T) =>
    z.object({
        ...responseBaseSchema.shape,
        results: z.array(itemSchema),
        totalCount: z.number(),
        columnOrder: z.array(z.string()).optional(),
    });

export type Entity = z.infer<typeof entitySchema>;
export type Auditable = z.infer<typeof auditableSchema>;
export type PaginationParams = z.infer<typeof paginationParamsSchema>;
export type MutationResponse = z.infer<typeof mutationResponseSchema>;

// Flattens the various error-payload shapes the API can return into plain strings:
//  - our shape:            errors: [{ message }]
//  - ASP.NET ProblemDetails: errors: { field: string[] }  (validation errors)
//  - bare strings:         errors: ["..."]
const collectErrorMessages = (errors: unknown): string[] => {
    if (Array.isArray(errors)) {
        return errors
            .map((e) => (typeof e === 'string' ? e : (e as { message?: string })?.message))
            .filter((m): m is string => typeof m === 'string' && m.length > 0);
    }
    if (errors && typeof errors === 'object') {
        return Object.values(errors as Record<string, unknown>).flatMap((v) =>
            Array.isArray(v)
                ? v.filter((x): x is string => typeof x === 'string')
                : typeof v === 'string'
                    ? [v]
                    : []
        );
    }
    return [];
};

// Parses a mutation response, tolerating endpoints that omit id/errors.
// Throws a clean Error when the API reports a failure (our `isError` flag, an
// HTTP `status` >= 400, or an ASP.NET ProblemDetails body) so react-query
// routes it to onError with a human-readable message rather than a raw dump.
export const parseMutationResponse = (response: unknown): MutationResponse => {
    const raw: Record<string, unknown> =
        typeof response === 'object' && response !== null ? { ...(response as Record<string, unknown>) } : {};

    const id = (raw.id ?? raw.locationTaskId ?? 0) as number;
    const status = typeof raw.status === 'number' ? raw.status : undefined;
    const messages = collectErrorMessages(raw.errors);
    const isError = raw.isError === true || (status !== undefined && status >= 400);

    if (isError) {
        const message =
            messages.join(', ') ||
            (typeof raw.title === 'string' ? raw.title : '') ||
            'The request failed';
        throw new Error(message);
    }

    return { ...raw, id, isError: false, errors: [] } as MutationResponse;
};
